<template>
	<div class="flex flex-wrap w-full relative" id="listings-container">
		<ul
			class="listings flex flex-wrap -mx-5"
			:class="[view == 'list' ? 'w-full -mt-30' : 'w-1/2 overflow-scroll -mt-10']"
			v-show="view != 'map'"
		>
			<!-- :class="{'listings-vertical' : orientation == 'vertical' }" -->
			<Property
				v-for="listing in listings"
				:key="listing.objectID"
				:id="'listing-'+listing.objectID"
				:listing="listing"
				:orientation="orientation"
				:requested-listing-type="requestedListingType"
				:requested-lease-rate="requestedLeaseRate"
			/>
			<!-- class="w-full sm:w-1/2 lg:w-1/3 2xl:w-1/4" -->
		</ul>

		<!-- Map View -->
		<div v-show="view == 'map'" class="w-full" id="map-container">
			<div class="map-wrapper h-full rounded-l w-full relative">
				<div
					v-if="!loading && !listings.length"
					class="absolute pin flex justify-center items-center z-3"
				>
					<div
						class="bg-blue400 p-40 rounded trans text-center text-white opacity-90"
					>No results, try broadening your search using the filters above.</div>
				</div>
				<div
					v-if="loading"
					class="absolute pin flex justify-center items-center z-3 bg-gray50 opacity-75"
				>
					<spinner :small="true" />
				</div>
				<div
					v-if="mapUnavailable"
					class="absolute pin flex justify-center items-center z-3 bg-blue200"
				>
					<div class="bg-blue400 p-40 rounded trans text-center text-white opacity-90">
						Map view is unavailable right now. Please use the property results or try again later.
					</div>
				</div>
				<div id="map" class="w-full h-full" style="font-family: inherit;"></div>
				<!-- Zoom Controls -->
				<div v-if="map" class="absolute pin-t pin-r m-15 bg-white rounded cursor-pointer">
					<div class>
						<div
							@click="map.zoomTo(map.getZoom() + 1)"
							class="px-12 py-5 h-40 flex items-center justify-center border-b-2"
						>
							<img src="/assets/img/plus.svg" alt="Zoom In"/>
						</div>
						<div class="w-full h-1 bg-gray100"></div>
						<div
							@click="map.zoomTo(map.getZoom() - 1)"
							class="px-12 py-5 h-40 flex items-center justify-center"
						>
							<img src="/assets/img/minus.svg" alt="Zoom Out"/>
						</div>
					</div>
				</div>
				<!-- Sat / Map -->
				<div
					v-if="map && mapStyle == 'streets-v9'"
					class="absolute pin-t pin-l m-15 p-10 px-20 bg-white rounded uppercase tracking-looser text-tiny font-secondary font-bold cursor-pointer"
					@click="setMapStyle('satellite-v9')"
				>Satellite</div>
				<div
					v-else-if="map"
					class="absolute pin-t pin-l m-15 p-10 px-20 bg-white rounded uppercase tracking-looser text-tiny font-secondary font-bold cursor-pointer"
					@click="setMapStyle('streets-v9')"
				>Map</div>
			</div>
		</div>
	</div>
</template>

<script>
	import Property from "./property.vue";

	import stateToAbbreviation from "../../helpers/stateToAbbr.js";
	import addCommas from "../../helpers/addCommas";
	import spinner from "../general/spinner.vue";

	// Renders commercial property cards and maintains the Mapbox result view.
	export default {
		props: {
			loading: {
				default: false
			},
			loadingQueued: {
				default: false
			},
			listings: {
				default: null
			},
			orientation: {
				default: "horizontal"
			},
			view: {
				default: "list"
			},
			query: {
				default: false
			},
			requestedListingType: {
				default: null
			},
			requestedLeaseRate: {
				default: null
			}
		},
		data() {
			return {
				map: null,
				mapUnavailable: false,
				mapStyle: "streets-v9",
				mapOffsetTop: null,
				windowScrollTop: null,
				navHeight: 120,
				height: null,
				firstLoad: true,
				types: {
					land: {
						color: "text-green500",
						icon: "land"
					},
					office: {
						color: "text-blue500",
						icon: "commercial"
					},
					retail: {
						color: "text-purple500",
						icon: "retail"
					},
					residential: {
						color: "text-red500",
						icon: "townhouse"
					},
					industrial: {
						color: "text-orange500",
						icon: "industrial"
					},
					specialty: {
						color: "text-yellow500",
						icon: "other"
					},
					healthcare: {
						color: "text-pink500",
						icon: "health"
					},
					hospitality: {
						color: "text-magenta500",
						icon: "condo"
					},
					entertainment: {
						color: "text-teal500",
						icon: "entertainment"
					},
					package: {
						color: "text-gray500",
						icon: "package"
					},
					combinedSpace: {
						color: "text-gray500",
						icon: "package"
					}
				}
			};
		},
		mounted() {
			window.addEventListener("scroll", this.handleScroll);
			window.addEventListener("resize", this.handleResize);
			this.handleScroll();
			this.handleResize();
		},
		unmounted() {
			window.removeEventListener("scroll", this.handleScroll);
			window.removeEventListener("resize", this.handleResize);
			if (this.map) {
				this.map.remove();
				this.map = null;
			}
		},
		emits: ['update-map'],
		updated() {
			var vm = this;
			if (this.view == "map" && vm.map) {
				vm.removeListings();
				vm.addListings();
			}
		},
		watch: {
			view: {
				immediate: true,
				handler() {
					var vm = this;

					if (this.view == "map" && !vm.map) {
						vm.$nextTick(function() {
							vm.createMap();
						});
					}

					if (this.view == "list") {
						if (this.map) this.map.remove();
						this.map = null;
						this.mapUnavailable = false;
					}
				}
			},
			'listings.length': {
				handler() {
					if (this.view == "map" && this.firstLoad && this.listings.length) {
						this.removeListings();
						var locations = this.addListings();
						this.zoomToBounds(locations);
						this.firstLoad = false;
					}
				}
			}
		},
		components: {
			Property,
			spinner
		},
		computed: {},
		methods: {
			setMapStyle(style) {
				if (!this.map) {
					return;
				}

				this.mapStyle = style;
				this.map.setStyle("mapbox://styles/mapbox/" + style);
			},
			handleScroll() {
				this.mapOffsetTop = document.getElementById(
					"listings-container"
				).offsetTop;
				this.windowScrollTop = document.documentElement.scrollTop;
			},
			handleResize() {
				// Define the height
				let height = document.documentElement.clientHeight;
				// If the height has changed, refacror the map
				if (height != this.height) {
					this.height = height;
					// this.map.resize()
				}
				// Define the width
				let width = document.documentElement.clientWidth;
				// define nav height by width
				if (width <= 1345) {
					this.navHeight = 120;
				} else {
					this.navHeight = 131;
				}
			},
			street(listing) {
				const { street1, street2 } = listing.address;
				return street1 + (street2 ? ", " + street2 : "");
			},
			cityStateZip(listing) {
				const { city, state, zip } = listing.address;
				return `${city}, ${stateToAbbreviation(state) || state} ${zip}`;
			},
			price(listing) {
				if (!listing) return '';

				if (listing.undisclosedPrice) return 'Undisclosed Price';
				if (listing.negotiableRent) return 'Negotiable Rent';

				const pricing = listing.pricing || {};
				const salePrice = pricing.sale?.price;
				const leasePriceMo = pricing.lease?.mo;
				const leasePriceSqft = pricing.lease?.sf?.min;
				const selectedUnit = pricing.lease?.selectedUnit;
				const moPrice = leasePriceMo !== null && leasePriceMo !== undefined
					? `$${this.formatPrice(leasePriceMo)}/mo`
					: '';
				const sfPrice = leasePriceSqft !== null && leasePriceSqft !== undefined
					? `$${this.formatPrice(leasePriceSqft)}/sf`
					: '';

				if (salePrice && this.requestedListingType !== 'lease') {
					return `$${this.formatPrice(salePrice)}`;
				}

				if (pricing.lease && this.requestedListingType !== 'sale') {
					if (this.requestedLeaseRate === 'mo') {
						return moPrice || sfPrice;
					}
					if (this.requestedLeaseRate === 'sf') {
						return sfPrice || moPrice;
					}
					if (selectedUnit === 'mo') {
						return moPrice || sfPrice;
					}
					if (selectedUnit === 'sqft') {
						return sfPrice || moPrice;
					}
				}

				return ''; // fallback
			},
			propertyLabel(listing) {
				let propertyType = listing.propertyType.label;
				let listingType = listing.listingType;
				let status = listing.status.label;

				let listingTypeLabel = "";

				if (listing.settings.inPackageOnly && listing.package) {
					listingTypeLabel = "In " + listing.package.type.label;
				} else {
					if (status == "Closed") {
						listingTypeLabel = "";
						if (listingType.sale) {
							listingTypeLabel = "Sold";
						}
						if (listingType.sale && listingType.lease) {
							listingTypeLabel = "Closed";
						}
						if (listingType.lease) {
							listingTypeLabel = "Leased";
						}
					} else {
						listingTypeLabel = "For ";
						if (listingType.sale) {
							listingTypeLabel += "Sale";
						}
						if (listingType.sale && listingType.lease) {
							listingTypeLabel += " & ";
						}
						if (listingType.lease) {
							listingTypeLabel += "Lease";
						}
					}
				}

				// Determine base label
				let propertyLabel = propertyType;

				// Add Listing Type
				propertyLabel += " • " + listingTypeLabel;

				return propertyLabel;
			},
			iconClasses(listing) {
				var iconClasses = "";
				var propertyType = listing.propertyType.value;

				var type = this.types[propertyType];

				iconClasses += type.defaultColor + " ";

				// Define the size and icon
				iconClasses += "icon-m-" + type.icon;

				return iconClasses;
			},
			removeListings() {
				$(".map-marker").remove();
			},
			addListings() {
				var vm = this;
				var locations = {
					type: "Locations",
					features: []
				};

				if (!window.mapboxgl) {
					vm.mapUnavailable = true;
					return locations;
				}

				if (!vm.map || !vm.listings.length) {
					return locations;
				}

				if (vm.listings.length) {
					for (const listing of vm.listings) {
						if (listing.entryType == "property") {
							locations.features.push({
								geometry: {
									type: "Point",
									coordinates: [
										listing.address.lng,
										listing.address.lat
									]
								},
								listing: listing
							});
						}
					}

					locations.features.forEach(function(marker) {
						// create a DOM element for the marker
						var el = document.createElement("div");
						// el.className = 'map-marker icon-s-single-family';
						var listing = marker.listing;
						var listingURL = listing.url;
						var details = "";


						var socialVars = ''
						// if (listing.photos.length) {
						// 	const urlImage = encodeURIComponent(listing.photos[0]);
						// 	socialVars = `?image=${urlImage}`
						// }

						listingURL += `${socialVars}`;

						if (
							listing.sf &&
							listing.sf.min > 0 &&
							listing.sf.max > 0
						) {
							details += `
							<div class="flex px-10">
								<span class="icon-m-measure text-gray300"></span>
								<div class="listing-info-item ml-10">${
									listing.sf.min != listing.sf.max
										? addCommas(listing.sf.min) +
										  "-" +
										  addCommas(listing.sf.max)
										: addCommas(listing.sf.min)
								} sf</div>
							</div>`;
						}

						if (listing.acres > 0) {
							details += `
							<div class="flex px-10">
								<span class="icon-m-tree text-gray300"></span>
								<div class="listing-info-item ml-10">${addCommas(listing.acres)} acres</div>
							</div>`;
						}

						if (listing.package) {
							details +=
								`
							<div class="flex px-10 ml-auto">` +
								(listing.settings.inPackageOnly
									? `
									<span class="icon-m-package-only text-gray300"></span>`
									: `
									<span class="icon-m-package-included text-gray300"></span>`) +
								`
							</div>`;
						}

						el.className =
							"map-marker " +
							vm.types[listing.propertyType.value].color.slice(
								5,
								-3
							) +
							" icon-s-" +
							vm.types[listing.propertyType.value].icon;

						// var propertyLabel = vm.propertyLabel(listing)

						// add marker to map
						new window.mapboxgl.Marker(el)
							.setLngLat(marker.geometry.coordinates)
							.setPopup(
								new window.mapboxgl.Popup({ offset: 0 }) // add popups
									.setHTML(
										`
								<div class="listing-item" style="width: 300px;">
									<a class="listing-item-header cursor-pointer" href="${listingURL}" style="z-index: initial;">` +
											(listing.photos.length
												? `<span class="bg-blue200 cursor-pointer absolute pin bg-cover bg-center" style="background-image: url(${listing.photos[0]}); z-index: -1;"
										></span>`
												: `<div class="absolute pin bg-gray700"><div class="w-full h-full bg-cover bg-center" style="background-image: url('/assets/img/properties/${listing.propertyType.value}.jpg'); z-index: -1; filter: blur(4px);">
											<div class="w-full h-full" style="background-color: rgba(27, 50, 61, 0.8);"></div>
										</div></div>`) +
											`</a>
									<div class="listing-item-details-wrapper" style="padding: 0px">
										<a class="listing-item-details cursor-pointer" style="margin-top: 0px; border-radius: 0px;" href="${listingURL}">
											<div class="listing-item-type" style="top: 0px; left: 30px;">
												<div class="absolute pin opacity-40 rounded-b ${vm.types[
													listing.propertyType.value
												].color.replace(
													"text-",
													"bg-"
												)}"></div>
												<div class="relative">${vm.propertyLabel(listing)}</div>
											</div>

											<div class="listing-item-details-top" style="padding: 25px; margin-top: 0px">
												<div class="flex items-center ${listing.propertyType.value}">
													<span class="-ml-3 listing-item-icon ${vm.iconClasses(listing)}"></span>` +
											(listing.settings.inPackageOnly
												? `<ListingIcon :color="${vm.types["package"].color}" icon="package-only" class="ml-10" />`
												: "") +
											(listing.settings.inPackageOnly &&
											listing.package
												? `<div class="ml-15 text-gray700 font-bold font-secondary uppercase tracking-loose text-tiny bg-gray50 rounded py-5 px-8">In ${listing.package.type.label} Only</div>`
												: `<div class="ml-15 text-xl text-gray700 font-secondary font-bold">${vm.price(
														listing
												  )}</div>`) +
											`</div>

												<div class="mt-20">
													<div class="font-bold text-gray700">${vm.street(listing)}</div>
													<div class="mt-10 text-sm">${vm.cityStateZip(listing)}</div>
												</div>

											</div>
											<div class="border-t border-solid border-gray100">
												<div class="-mx-10 p-25 flex">
													${details}
												</div>
											</div>
										</a>
									</div>
								</div>
								`
									)
							)
							.setOffset([0, -18])
							.addTo(vm.map);
					});

					this.zoomToBounds(locations);
				}

				return locations;
			},
			zoomToBounds(locations) {
				var vm = this;

				if (!this.map || !locations || !locations.features.length) {
					return;
				}

				var bounds = new window.mapboxgl.LngLatBounds();

				locations.features.forEach(function(feature) {
					bounds.extend(feature.geometry.coordinates);
				});

				this.map.fitBounds(bounds, { padding: 100, maxZoom: 13 });

				setTimeout(function() {
					vm.$emit("update-map", vm.map.getBounds());
				}, 1000);
			},
			createMap() {
				var vm = this;

				if (!window.mapboxgl) {
					vm.mapUnavailable = true;
					return null;
				}

				window.mapboxgl.accessToken =
					"pk.eyJ1Ijoic3RlYWRmYXN0ZGVzaWduZmlybSIsImEiOiJjanA4cjJua3gyMms4M3FwN2h2dGtpY2FsIn0.WNrUU--j6DaF_aV6PiTrcA";

				try {
					vm.map = new window.mapboxgl.Map({
						container: "map",
						style: "mapbox://styles/mapbox/" + this.mapStyle,
						scrollZoom: false,
						zoomControl: false,
						zoom: 10
					});
				} catch (error) {
					console.warn("Mapbox failed to initialize.", error);
					vm.mapUnavailable = true;
					return null;
				}

				vm.mapUnavailable = false;

				vm.map.setZoom(12);
				vm.map.setCenter({ lat: 35.906057, lng: -78.863758 });

				// vm.map.addControl(new mapboxgl.NavigationControl(), 'top-left');
			},
			formatPrice(value) {
				if (value === null || value === undefined || value === '') {
					return '';
				}

				// Strip any non-numeric characters (except for decimal point)
				const formattedValue = value.toString().replace(/[^0-9.]/g, '');

				// Then convert to number
				const num = Number(formattedValue);

				if (isNaN(num)) {
					return value;
				}

				let result = num.toFixed(2).replace('.00', '');
				return addCommas(result);
			}
		}
	};
</script>
